import { buildSearchStatus, type SearchEngine } from "@coveo/headless";
import { type RefObject, useCallback, useEffect, useRef } from "react";

type ExecuteFirstSearch = () => Promise<void>;

type LazyFirstSearchResult = {
  resultsTriggerRef: RefObject<HTMLDivElement | null>;
  handleSearchInterfaceReady: (
    executeFirstSearch: ExecuteFirstSearch
  ) => Promise<void>;
  requestFirstSearch: () => void;
};

function hasExplicitSearchIntent(hash: string): boolean {
  const parameters = new URLSearchParams(hash.replace(/^#/, ""));

  const query = parameters.get("q")?.trim();

  if (query) {
    return true;
  }

  const firstResult = Number(parameters.get("firstResult"));

  return Number.isFinite(firstResult) && firstResult > 0;
}

export function useLazyCoveoFirstSearch(
  engine: SearchEngine | undefined
): LazyFirstSearchResult {
  const resultsTriggerRef = useRef<HTMLDivElement | null>(null);
  const executeFirstSearchRef = useRef<ExecuteFirstSearch | null>(null);

  const searchRequestedRef = useRef(false);
  const searchStartedRef = useRef(false);

  const executeStoredFirstSearch = useCallback(async () => {
    if (!engine || searchStartedRef.current) {
      return;
    }

    const executeFirstSearch = executeFirstSearchRef.current;

    // Atomic may not be initialized yet.
    if (!executeFirstSearch) {
      return;
    }

    searchStartedRef.current = true;

    try {
      await executeFirstSearch();
    } catch (error) {
      searchStartedRef.current = false;

      console.error("[Coveo] Failed to execute the initial search", error);
    }
  }, [engine]);

  const requestFirstSearch = useCallback(() => {
    if (!engine) {
      return;
    }

    searchRequestedRef.current = true;
    void executeStoredFirstSearch();
  }, [engine, executeStoredFirstSearch]);

  const handleSearchInterfaceReady = useCallback(
    async (executeFirstSearch: ExecuteFirstSearch) => {
      if (!engine) {
        return;
      }

      executeFirstSearchRef.current = executeFirstSearch;

      const shouldLoadImmediately = hasExplicitSearchIntent(
        window.location.hash
      );

      if (shouldLoadImmediately) {
        searchRequestedRef.current = true;
      }

      if (searchRequestedRef.current) {
        await executeStoredFirstSearch();
      }
    },
    [engine, executeStoredFirstSearch]
  );

  useEffect(() => {
    if (!engine) {
      return;
    }

    const searchStatus = buildSearchStatus(engine);

    const unsubscribe = searchStatus.subscribe(() => {
      if (searchStatus.state.firstSearchExecuted) {
        searchStartedRef.current = true;
      }
    });

    return () => {
      unsubscribe();

      executeFirstSearchRef.current = null;
      searchRequestedRef.current = false;
      searchStartedRef.current = false;
    };
  }, [engine]);

  useEffect(() => {
    if (!engine) {
      return;
    }

    const target = resultsTriggerRef.current;

    if (!target) {
      return;
    }

    if (!("IntersectionObserver" in window)) {
      requestFirstSearch();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }

        requestFirstSearch();
        observer.disconnect();
      },
      {
        root: null,
        rootMargin: "400px 0px",
        threshold: 0,
      }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [engine, requestFirstSearch]);

  return {
    resultsTriggerRef,
    handleSearchInterfaceReady,
    requestFirstSearch,
  };
}
